import React from 'react';
import { Box, Card, CardContent, Typography, Chip, Divider } from '@mui/material';
import { Referral, IngestionType } from '~/types/transition';
import SourceBadge from '../common/SourceBadge';

interface ReferralIntakeCardProps {
  referral: Referral;
}

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

const LABEL_STYLE = {
  textTransform: 'uppercase' as const,
  fontWeight: 600,
  letterSpacing: 0.5,
  fontSize: '0.7rem',
  color: 'grey.500',
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" sx={LABEL_STYLE}>{label}</Typography>
    <Box sx={{ mt: 0.25 }}>{children}</Box>
  </Box>
);

const ReferralIntakeCard: React.FC<ReferralIntakeCardProps> = ({ referral }) => {
  const isAuto = referral.ingestionType === IngestionType.AUTO;

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
          Referral
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1.5,
          }}
        >
          <Field label="Received">
            <Typography variant="body2">{formatTimestamp(referral.receivedAt)}</Typography>
          </Field>

          <Field label="Source">
            <Box sx={{ mt: 0.25 }}>
              <SourceBadge source={referral.source} size="small" />
            </Box>
          </Field>

          <Field label="Ingestion">
            <Chip
              label={isAuto ? 'Auto-ingested' : 'Assisted intake'}
              size="small"
              variant="outlined"
              color={isAuto ? 'info' : 'default'}
              sx={{ fontSize: '0.72rem', mt: 0.25 }}
            />
          </Field>

          <Field label="Requested Program">
            <Typography variant="body2">{referral.patient.requestedProgram}</Typography>
          </Field>

          {/* Divider spanning both columns */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Divider />
          </Box>

          <Field label="Referring Provider">
            <Typography variant="body2">{referral.patient.referringProvider}</Typography>
            <Typography variant="body2" color="text.secondary">
              {referral.patient.referringFacility}
            </Typography>
          </Field>

          <Field label="Insurance">
            <Typography variant="body2">{referral.patient.insurance}</Typography>
          </Field>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReferralIntakeCard;
